import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
  getLeaderboardCycleTopUsers,
  LeaderboardCycle,
  CycleTopUser,
  CycleTopUsersResult,
} from '../../../services/redpacketService';
import { useTranslate } from 'react-admin';

interface TopUsersDialogProps {
  record: LeaderboardCycle;
  onClose: () => void;
}

const TopUsersDialog = ({ record, onClose }: TopUsersDialogProps) => {
  const t = useTranslate();
  const LIMIT_OPTIONS = [
    { value: '', label: t('module.redpacket.default100') },
    { value: '10', label: '10' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
    { value: '500', label: '500' },
  ];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CycleTopUsersResult | null>(null);
  const [limit, setLimit] = useState<string>('');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const limitNum = limit ? Number(limit) : undefined;
        const result = await getLeaderboardCycleTopUsers(record.id, limitNum);
        setData(result);
      } catch (err: any) {
        setError(err.message || t('module.redpacket.failedToFetchTopUsers'));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [record.id, limit,t]);

  const formatTime = (ts?: number) => (ts ? new Date(ts).toLocaleString() : '-');

  return (
    <Dialog open maxWidth="lg" fullWidth>
      <DialogTitle>
        {t('module.redpacket.topUsersCycle')}{record.cycleNumber}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {record.status} • Start: {new Date(record.startTime).toLocaleString()} • End:{' '}
          {new Date(record.endTime).toLocaleString()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('module.redpacket.limit')}</InputLabel>
            <Select
              value={limit}
              label={t('module.redpacket.limit')}
              onChange={(e) => setLimit(e.target.value)}
              disabled={loading}
            >
              {LIMIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'default'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {data && !loading && (
            <Typography variant="body2" color="text.secondary">
              {t('module.redpacket.totalUsers')+data.totalCount}
            </Typography>
          )}
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : data ? (
          <Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.redpacket.rank')}</TableCell>
                    <TableCell>{t('module.redpacket.userIdCol')}</TableCell>
                    <TableCell>{t('module.redpacket.walletAddress')}</TableCell>
                    <TableCell align="right">{t('module.redpacket.tradingVolume')}</TableCell>
                    <TableCell>{t('module.redpacket.firstTradeAt')}</TableCell>
                    <TableCell>{t('module.redpacket.lastTradeAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {t('module.redpacket.noUsersFound')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.users.map((u: CycleTopUser) => (
                      <TableRow key={u.userId}>
                        <TableCell>{u.rank}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {u.userId}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {u.walletAddress}
                        </TableCell>
                        <TableCell align="right">{u.tradingVolume}</TableCell>
                        <TableCell>{formatTime(u.firstTradeAt)}</TableCell>
                        <TableCell>{formatTime(u.lastTradeAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('module.redpacket.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TopUsersDialog;
